import React, { useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n.ts';

/**
 * Generic confirmation dialog (Refs #778).
 *
 * A11y-compliant: role="dialog" + aria-modal, focus-trap met Tab/Shift+Tab
 * cycling, Escape-toets sluit, focus wordt gerestored naar de trigger-element
 * dat de dialog opende. Buiten klikken sluit ook (consistent met UX-conventie
 * voor non-destructive dialogs; voor destructieve flows kan de caller
 * `variant="danger"` gebruiken om visueel te waarschuwen).
 */
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'default',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Focus management: bewaar trigger bij open, restore bij close.
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement as HTMLElement;
      // Focus naar de confirm-knop (volgende frame zodat de DOM klaar is).
      const id = window.setTimeout(() => confirmRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    } else if (triggerRef.current) {
      try {
        triggerRef.current.focus();
      } catch {
        /* trigger may have been unmounted */
      }
    }
  }, [open]);

  // Keyboard: Escape sluit, Tab/Shift+Tab cyclen binnen de dialog (focus-trap).
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onCancel}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-desc"
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-dialog-title" className="text-xl font-bold text-pt-forest">
          {title}
        </h2>
        <div id="confirm-dialog-desc" className="mt-3 text-sm text-pt-sage">
          {description}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-semibold text-pt-sage hover:text-pt-forest"
          >
            {cancelLabel ?? t('cancel')}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-semibold rounded ${
              variant === 'danger'
                ? 'bg-pt-ruby text-white hover:bg-pt-ruby-hover'
                : 'bg-pt-forest text-pt-gold hover:bg-pt-forest/90'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;
